import { useFormContext } from 'react-hook-form';
import { useSendWithdrawalCode } from '../../../../../../api';
import { WithdrawFormCreateDto } from '../../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import { useTranslation } from '../../../../../../hooks';
import { ConfirmationCode } from '../../../../../Confirmation/ConfirmationCode';

/**
 * TransactionsModalsWithdrawContentConfirmationCode Component
 * This component is responsible for handling the confirmation code input and sending the withdrawal code.
 * It utilizes translation for labels and manages form state using React Hook Form.
 *
 * @returns {JSX.Element} The rendered component for entering and sending a confirmation code.
 *
 * @example
 * // Usage example:
 * <TransactionsModalsWithdrawContentConfirmationCode />
 */
export default function TransactionsModalsWithdrawContentConfirmationCode() {
  // Retrieve the translation function for the 'withdraw' namespace
  const { t } = useTranslation('withdraw');
  // Retrieve form context methods for watching and setting form values
  const { watch, setValue } = useFormContext<WithdrawFormCreateDto>();
  // Watch the 'emailId' field in the form
  const emailId: bigint = watch('crypto.emailId');

  // Destructure the sendWithdrawalCode function and its state from the custom hook
  const {
    isSuccess,
    sendWithdrawalCode,
    isLoading: isLoadingValidationCode,
  } = useSendWithdrawalCode();

  /**
   * handleSendSms Function
   * This function triggers the sending of the withdrawal code via SMS.
   *
   * @returns {void}
   *
   * @example
   * handleSendSms();
   */
  function handleSendSms() {
    sendWithdrawalCode({
      emailId,
    });
  }

  /**
   * handleChange Function
   * This function updates the 'code' field in the form with the provided value.
   *
   * @param {string} value - The new value for the 'code' field.
   * @returns {void}
   *
   * @example
   * handleChange('123456');
   */
  function handleChange(value: string) {
    setValue('crypto.code', value);
  }

  // Render the ConfirmationCode component with the necessary props
  return (
    <ConfirmationCode
      label={t('withdraw_sms_text')}
      handleChangeCode={handleChange}
      handleSendSms={handleSendSms}
      isLoadingValidationCode={isLoadingValidationCode}
      isSuccess={isSuccess}
    />
  );
}
